import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle } from 'react'
import { callAPI, getTheme, uploadToS3 } from 'helpers'
import { get } from 'lodash'

// Components
import styled from 'styled-components'
import { Input, Icon, Radio } from 'antd'
import FormGroup from 'components/FormGroup'
import Label from 'components/Label'

export default forwardRef(({ questionNo, question }, ref) => {
  const formRefs = Array(5).fill().map(() => useRef())
  const questionId = get(question, 'examQuestionId')
  const [correctAnswer, setCorrectAnswer] = useState(null)
  const [correctAnswerError, setCorrectAnswerError] = useState('')

  const [questionHead, setQuestionHead] = useState()
  const [answers, setAnswers] = useState([])

  useEffect(() => {
    if (questionId) {
      setQuestionHead({
        text: question.title,
        imageURL: question.imageURL,
        imageWidth: question.imageWidth,
        imageHeight: question.imageHeight
      })
      setAnswers(Array(4).fill().map((_, index) => {
        const answerNo = index + 1
        return {
          text: question[`answer${answerNo}Text`],
          imageURL: question[`answer${answerNo}ImageURL`],
          imageWidth: question[`answer${answerNo}ImageWidth`],
          imageHeight: question[`answer${answerNo}ImageHeight`]
        }
      }))
      setCorrectAnswer(question.correctAnswer)
    }
  }, [questionId])

  const uploadFile = async (file) => {
    if (!file) return Promise.resolve(null)
    try {
      const { url } = await callAPI({
        method: 'POST',
        url: '/s3/signed-url',
        body: {
          key: `${new Date().getTime().toString(16)}.${file.type}`,
          contentType: file.type
        }
      })
      return uploadToS3({ url, file })
    } catch (error) {
      console.error('Error while trying to upload image to S3', error.message)
    }
  }

  useImperativeHandle(ref, () => ({
    getFormData: () => {
      const data = formRefs.map(ref => ref.current.getFormData())
      setCorrectAnswerError(!correctAnswer && 'กรุณาระบุเฉลยคำตอบ')
      if (data.some(_value => !_value)) return false
      if (!correctAnswer) return false
      const [question, answer1, answer2, answer3, answer4] = data
      const imageResolve = ({ imageRemoved, ...props }, key) =>
        imageRemoved ? null : props[key]
      return {
        title: question.text,
        imageWidth: imageResolve(question, 'imageWidth'),
        imageHeight: imageResolve(question, 'imageHeight'),
        answer1Text: answer1.text,
        answer1ImageWidth: imageResolve(answer1, 'imageWidth'),
        answer1ImageHeight: imageResolve(answer1, 'imageHeight'),
        answer2Text: answer2.text,
        answer2ImageWidth: imageResolve(answer2, 'imageWidth'),
        answer2ImageHeight: imageResolve(answer2, 'imageHeight'),
        answer3Text: answer3.text,
        answer3ImageWidth: imageResolve(answer3, 'imageWidth'),
        answer3ImageHeight: imageResolve(answer3, 'imageHeight'),
        answer4Text: answer4.text,
        answer4ImageWidth: imageResolve(answer4, 'imageWidth'),
        answer4ImageHeight: imageResolve(answer4, 'imageHeight'),
        correctAnswer
      }
    },
    uploadAllFiles: async () => {
      const data = formRefs.map(ref => ref.current.getFormData())
      setCorrectAnswerError(!correctAnswer && 'กรุณาระบุเฉลยคำตอบ')
      if (data.some(_value => !_value)) return false
      if (!correctAnswer) return false
      const [question, answer1, answer2, answer3, answer4] = data

      const imageURL = await uploadFile(question.file)
      const answer1ImageURL = await uploadFile(answer1.file)
      const answer2ImageURL = await uploadFile(answer2.file)
      const answer3ImageURL = await uploadFile(answer3.file)
      const answer4ImageURL = await uploadFile(answer4.file)

      console.log('uploadAllFiles result', {
        question,
        answer1,
        answer2,
        answer3,
        answer4,
        imageURL: question.file ? imageURL : question.imageRemoved ? null : question.imageURL,
        answer1ImageURL: answer1.file ? answer1ImageURL : answer1.imageRemoved ? null : question.answer1ImageURL,
        answer2ImageURL: answer2.file ? answer2ImageURL : answer2.imageRemoved ? null : question.answer2ImageURL,
        answer3ImageURL: answer3.file ? answer3ImageURL : answer3.imageRemoved ? null : question.answer3ImageURL,
        answer4ImageURL: answer4.file ? answer4ImageURL : answer4.imageRemoved ? null : question.answer4ImageURL
      })
      return Promise.resolve({
        imageURL: question.file ? imageURL : question.imageRemoved ? null : question.imageURL,
        answer1ImageURL: answer1.file ? answer1ImageURL : answer1.imageRemoved ? null : question.answer1ImageURL,
        answer2ImageURL: answer2.file ? answer2ImageURL : answer2.imageRemoved ? null : question.answer2ImageURL,
        answer3ImageURL: answer3.file ? answer3ImageURL : answer3.imageRemoved ? null : question.answer3ImageURL,
        answer4ImageURL: answer4.file ? answer4ImageURL : answer4.imageRemoved ? null : question.answer4ImageURL
      })
    },
    clear: () => {
      formRefs.forEach(ref => ref.current.clear())
      setCorrectAnswer('')
    }
  }))

  return (
    <ContentWrapper>
      <TextInputWithImage
        ref={formRefs[0]}
        questionId={questionId}
        label={`คำถามที่ ${questionNo}`}
        data={questionHead} />
      <Choices>
        {Array(4).fill().map((_, index) =>
          <TextInputWithImage
            key={index}
            ref={formRefs[index + 1]}
            questionId={questionId}
            label={`ตัวเลือกที่ ${index + 1}`}
            data={get(answers, index)} />
        )}
      </Choices>
      <div>
        <Label>เฉลยคำตอบ</Label>
        <Radio.Group
          value={correctAnswer}
          onChange={e => setCorrectAnswer(e.target.value)}>
          <Radio value={1}>ตัวเลือกที่ 1</Radio>
          <Radio value={2}>ตัวเลือกที่ 2</Radio>
          <Radio value={3}>ตัวเลือกที่ 3</Radio>
          <Radio value={4}>ตัวเลือกที่ 4</Radio>
        </Radio.Group>
        {correctAnswerError && !correctAnswer && <ErrorText>{correctAnswerError}</ErrorText>}
      </div>
    </ContentWrapper>
  )
})

const TextInputWithImage = forwardRef(({ questionId, label, ...props }, ref) => {
  const chooserRef = useRef()
  const imageRef = useRef()

  const [text, setText] = useState('')
  const [file, setFile] = useState(null)
  const [imageURL, setImageURL] = useState('')
  const [imageWidth, setImageWidth] = useState(null)
  const [imageHeight, setImageHeight] = useState(null)
  const [imageRemoved, setImageRemoved] = useState(false)
  const [error, setError] = useState()

  useEffect(() => {
    if (props.data) {
      const { text, imageURL, imageWidth, imageHeight } = props.data
      setText(text)
      setImageURL(imageURL)
      setImageWidth(imageWidth)
      setImageHeight(imageHeight)
    }
  }, [props.data])

  useEffect(() => {
    if (file) {
      imageRef.current.onload = () => {
        setImageWidth(imageRef.current.naturalWidth || null)
        setImageHeight(imageRef.current.naturalHeight || null)
      }
    }
  }, [file])

  const _onImageChosen = (e) => {
    const file = e.target.files[0]
    e.target.value = null
    setFile(file)
    setImageURL(URL.createObjectURL(file))
    setImageRemoved(false)
  }
  useImperativeHandle(ref, () => ({
    getFormData: () => {
      if (!text && !file) {
        setError(`กรุณากรอกข้อมูล${label}`)
        return false
      }
      const result = { text, file, imageURL, imageWidth, imageHeight, imageRemoved }
      return result
    },
    clear: () => {
      setText('')
      setFile(null)
      setImageURL('')
      setImageWidth(null)
      setImageHeight(null)
      setImageRemoved(false)
    }
  }))

  return (
    <CustomFormGroup>
      <Label>{label}</Label>
      <input type='file'
        ref={chooserRef}
        style={{ display: 'none' }}
        onChange={_onImageChosen} />
      <Input type='text'
        value={text}
        onChange={e => setText(e.target.value)}
        addonAfter={<CustomIcon type='picture' onClick={() => chooserRef.current.click()} />} />
      {error && !text && !file && <ErrorText>{error}</ErrorText>}
      {imageURL && (
        <div style={{ textAlign: 'center' }}>
          <FormImageWrapper>
            <FormImage
              ref={imageRef}
              style={{ width: imageWidth || 'auto' }}
              src={imageURL} />
            <Icon
              type='close-circle'
              onClick={() => {
                setFile(null)
                setImageURL('')
                setImageRemoved(true)
              }} />
          </FormImageWrapper>
        </div>
      )}
    </CustomFormGroup>
  )
})

const ContentWrapper = styled.div`

`
const CustomFormGroup = styled(FormGroup)`
  > div > span.katex-display { margin-bottom: 0px; }
`
const Choices = styled.div`
  margin-left: 24px;
  padding-left: 12px;
  border-left: 1px solid ${getTheme`colors.border`};
`
const CustomIcon = styled(Icon)`
  cursor: pointer;
  transition: opacity 0.3s ease;
  &:hover { opacity: 0.8; }
`
const FormImageWrapper = styled.div`
  margin: 30px auto;
  padding: 15px;
  text-align: center;
  position: relative;
  display: inline-block;
  vertical-align: top;
  border: 1px solid ${getTheme`colors.border`};
  > i {
    cursor: pointer;
    position: absolute;
    top: -10px;
    right: -10px;
    font-size: 20px;
    background-color: white;
    color: red;
  }
`
const FormImage = styled.img`
  max-width: 100%;
`
const ErrorText = styled.div`
  margin-top: 5px;
  font-size: 12px;
  color: ${getTheme`colors.red`};
`



// WEBPACK FOOTER //
// ./src/components/exams/QuestionForm.js